// insertMapNode.js — Insert a map embed block inline.
// Map content lives in the report's single shared Y.XmlFragment (see mapFragmentInit.js) — not
// in this node's attrs. Inserting a node here just gives it its own identity (nodeId) and makes
// sure the shared fragment exists; it never creates a new/independent map.

import {generateUUID} from '../helpers/utils';
import useYDocStore from '../../../stores/useYDocStore';
import {initializeSharedMapFragment} from '../../map/mapFragmentInit';
import {insertAtomBlockNode} from './insertAtomBlockNode';

// map-node-dtg-default-on-insert bug fix: day-aligned "now", UTC start-of-day — matches
// useDtgRange.js's (unexported) toStartOfDayMs/defaultEndMs convention (date-only, no hours/
// minutes). Not imported from there: those helpers are private to that module, and that module
// exists to be a React hook (pulls in useState/useEffect/useCallback/useMemo/useRef and the
// dayjs+dayjs/plugin/utc dependency) — pulling that in here just for one date-truncation
// one-liner would add real dependency weight to this plain (non-React) PM command helper for no
// benefit; native `Date.UTC` does the same day-truncation with zero extra imports.
function todayStartOfDayUtcMs() {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/**
 * Synchronously inserts a new map node at the current selection (or at
 * `opts.range` when provided — see insertAtomBlockNode).
 * A stable nodeId UUID is generated here and stored in the node attrs, identifying this embed
 * (its own window/view/DTG-scrub state) — not a fragment it owns. The report's single shared
 * Y.XmlFragment is ensured to exist (created once, on the first map node any report gets).
 *
 * map-node-dtg-default-on-insert bug fix: `dtgSelectedMs` is stamped with today's date (UTC
 * start-of-day) here rather than left at the schema's bare `null` default, so a brand-new embed
 * always opens with its DTG scrub already selected to the current date instead of showing up
 * unselected (see MapNodeChrome.updateDtg's display-only "latest marking" fallback, which only
 * ever resolves to something for embeds that already have markings — a fresh embed has none).
 *
 * @param {EditorView} view
 * @param {Object} [opts] - forwarded to insertAtomBlockNode (range/meta)
 * @returns {boolean}
 */
export function insertMapNode(view, opts = {}) {
    if (!view) return false;
    const {schema} = view.state;
    const mapType = schema.nodes.map;
    if (!mapType) return false;

    const nodeId = generateUUID();
    const mapNode = mapType.create({nodeId, viewId: null, dtgSelectedMs: todayStartOfDayUtcMs()});

    const inserted = insertAtomBlockNode(view, mapNode, opts);
    if (!inserted) return false;

    const ydoc = useYDocStore.getState().ydoc;
    if (ydoc) {
        initializeSharedMapFragment(ydoc);
    }

    return true;
}
